package io.horion.auth.presentation.ui.components.profile

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.api.models.domain.LoginMethod
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.theme.AuthColors
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgba

@Composable
fun ProfileSecuritySection(viewModel: AuthViewModel) {
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val strings = AppLanguage.strings
    val state = viewModel.uiState

    var showPasswordForm by remember { mutableStateOf(false) }
    var showDisable2FAConfirmation by remember { mutableStateOf(false) }
    val isEmailAuth = state.authMethod == LoginMethod.EMAIL

    // Fond du formulaire mot de passe selon le thème
    val pwdFormBg = if (colorMode.isLight) colors.inputBg else colors.surface

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(16.px)
            .backgroundColor(colors.surface)
            .border(1.px, LineStyle.Solid, colors.border)
            .styleModifier { property("box-shadow", "0 1px 3px rgba(0,0,0,0.04)") }
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px, topBottom = 20.px)
                .gap(10.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            FaShield(modifier = Modifier.fontSize(18.px).color(colors.primary))
            SpanText(
                strings.securityTitle,
                modifier = Modifier
                    .fontSize(AuthTypography.body)
                    .fontWeight(FontWeight.SemiBold)
                    .color(colors.onBackground)
            )
        }

        Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))

        if (state.passwordChangeSuccess) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 24.px, topBottom = 12.px)
                    .gap(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaCircleCheck(modifier = Modifier.color(colors.success).fontSize(14.px))
                SpanText(
                    strings.passwordChangeSuccess,
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.success)
                )
            }
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
        }

        SecurityRow(
            icon = { FaWrench(modifier = Modifier.fontSize(16.px).color(colors.mutedText)) },
            title = strings.securityPassword,
            subtitle = strings.securityPasswordSubtitle,
            subtitleColor = null,
            isLast = false,
            colors = colors,
            onClick = {
                showPasswordForm = !showPasswordForm
                if (!showPasswordForm) {
                    viewModel.onCurrentPasswordChange("")
                    viewModel.onNewPasswordChange("")
                    viewModel.onConfirmNewPasswordChange("")
                }
            }
        )


        if (showPasswordForm) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 24.px, topBottom = 16.px)
                    .backgroundColor(pwdFormBg)
                    .gap(12.px)
            ) {
                val targetChannel = if (isEmailAuth) state.userEmail else state.userPhone

                if (!state.passwordOtpSent) {
                    // ── ÉTAPE 1 : Demande d'envoi de code ──────────────────
                    SpanText(
                        "Un code de confirmation sera envoyé à $targetChannel pour valider la modification.",
                        modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.mutedText)
                    )

                    if (state.passwordChangeError != null) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(6.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(13.px))
                            SpanText(
                                state.passwordChangeError!!,
                                modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.error)
                            )
                        }
                    }

                    Row(modifier = Modifier.fillMaxWidth().gap(10.px).padding(top = 4.px)) {
                        AuthButton(
                            text = strings.profileCancelButton,
                            onClick = {
                                showPasswordForm = false
                                viewModel.resetPasswordForm()
                            },
                            disabled = state.isSendingPasswordOtp,
                            modifier = Modifier.weight(1).styleModifier { property("opacity", "0.6") }
                        )
                        AuthButton(
                            text = "Recevoir le code",
                            onClick = { viewModel.requestPasswordChangeOtp() },
                            loading = state.isSendingPasswordOtp,
                            modifier = Modifier.weight(1)
                        )
                    }
                } else {
                    // ── ÉTAPE 2 : Saisie du Code + Nouveau mot de passe ──────────────────
                    SpanText(
                        "Code envoyé à $targetChannel",
                        modifier = Modifier.fontSize(AuthTypography.tiny).fontWeight(FontWeight.SemiBold).color(colors.primary)
                    )

                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = state.code,
                        onValueChange = { viewModel.onChangeCode(it) },
                        placeholder = "Code de vérification (OTP)",
                        type = InputType.Text,
                        leadingIcon = { FaKey(modifier = Modifier.color(colors.secondary)) }
                    )

                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = state.newPassword,
                        onValueChange = { viewModel.onNewPasswordChange(it) },
                        placeholder = strings.newPasswordPlaceholder,
                        type = InputType.Password,
                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) }
                    )

                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = state.confirmNewPassword,
                        onValueChange = { viewModel.onConfirmNewPasswordChange(it) },
                        placeholder = strings.passwordConfirmHint,
                        type = InputType.Password,
                        leadingIcon = { FaLock(modifier = Modifier.color(colors.secondary)) }
                    )

                    // Affichage erreur globale
                    if (state.passwordChangeError != null) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(6.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(13.px))
                            SpanText(
                                state.passwordChangeError!!,
                                modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.error)
                            )
                        }
                    }

                    Row(modifier = Modifier.fillMaxWidth().gap(10.px).padding(top = 4.px)) {
                        AuthButton(
                            text = strings.profileCancelButton,
                            onClick = {
                                showPasswordForm = false
                                viewModel.resetPasswordForm()
                            },
                            disabled = state.isChangingPassword,
                            modifier = Modifier.weight(1).styleModifier { property("opacity", "0.6") }
                        )
                        AuthButton(
                            text = strings.profileSaveButton,
                            onClick = { viewModel.confirmPasswordReset() },
                            loading = state.isChangingPassword,
                            modifier = Modifier.weight(1)
                        )
                    }
                }
            }
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
        }


        val targetLabel = if (isEmailAuth) "Numéro de téléphone" else "Adresse Email"
        val currentTargetValue = if (isEmailAuth) state.userPhone else state.userEmail

        val subtitleText = when {
            state.is2FAEnabled -> "Sécurisé via ${currentTargetValue ?: targetLabel}"
            currentTargetValue != null -> "Vérification requise pour activer la 2FA ($currentTargetValue)"
            else -> "Ajouter un $targetLabel pour sécuriser votre compte"
        }

        SecurityRowWithToggle(
            icon = {
                if (isEmailAuth) {
                    FaPhone(modifier = Modifier.fontSize(16.px).color(colors.mutedText))
                } else {
                    FaEnvelope(modifier = Modifier.fontSize(16.px).color(colors.mutedText))
                }
            },
            title = "Validation 2FA par $targetLabel",
            subtitle = subtitleText,
            subtitleColor = if (state.is2FAEnabled) colors.success else null,
            enabled = state.is2FAEnabled,
            isLoading = state.isToggling2FA,
            onToggle = { enable ->
                if (enable) {
                    // Déclenche l'envoi de l'OTP (SMS si connecté par Email, Mail si connecté par Téléphone)
                    viewModel.initiateSecondaryChannelSetup()
                } else {
                    viewModel.disable2FA()
                }
            },
            isLast = false,
            colors = colors
        )


        if (state.show2FASetupModal) {
            SecondaryAuthSetupModal(
                viewModel = viewModel,
                colors = colors,
                isEmailAuth = isEmailAuth,
                onDismiss = { viewModel.cancel2FASetup() }
            )
        }

    }

//    if (state.show2FASetupModal) {
//        TwoFactorSetupModal(
//            viewModel = viewModel,
//            colors = colors,
//            onDismiss = { viewModel.cancel2FASetup() }
//        )
//    }


}


@Composable
private fun SecurityRow(
    icon: @Composable () -> Unit,
    title: String,
    subtitle: String,
    subtitleColor: com.varabyte.kobweb.compose.ui.graphics.Color?,
    isLast: Boolean,
    colors: AuthColors,
    onClick: () -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px, topBottom = 18.px)
                .cursor(Cursor.Pointer)
                .styleModifier { property("transition", "background 0.15s") }
                .onClick { onClick() },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(16.px)) {
                Box(
                    modifier = Modifier.size(36.px).borderRadius(10.px).backgroundColor(colors.inputBg),
                    contentAlignment = Alignment.Center
                ) { icon() }
                Column(modifier = Modifier.gap(3.px)) {
                    SpanText(title, modifier = Modifier.fontSize(AuthTypography.small).fontWeight(FontWeight.SemiBold).color(colors.onBackground))
                    SpanText(subtitle, modifier = Modifier.fontSize(AuthTypography.tiny).color(subtitleColor ?: colors.mutedText))
                }
            }
            FaChevronRight(modifier = Modifier.fontSize(12.px).color(colors.mutedText))
        }
        if (!isLast) Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
    }
}

@Composable
private fun SecurityRowWithToggle(
    icon: @Composable () -> Unit,
    title: String,
    subtitle: String,
    subtitleColor: com.varabyte.kobweb.compose.ui.graphics.Color?,
    enabled: Boolean,
    isLoading: Boolean = false,
    onToggle: (Boolean) -> Unit,
    isLast: Boolean,
    colors: AuthColors,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px, topBottom = 18.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(16.px)) {
                Box(
                    modifier = Modifier.size(36.px).borderRadius(10.px).backgroundColor(colors.inputBg),
                    contentAlignment = Alignment.Center
                ) { icon() }
                Column(modifier = Modifier.gap(3.px)) {
                    SpanText(title, modifier = Modifier.fontSize(AuthTypography.small).fontWeight(FontWeight.SemiBold).color(colors.onBackground))
                    SpanText(subtitle, modifier = Modifier.fontSize(AuthTypography.tiny).color(subtitleColor ?: colors.mutedText))
                }
            }

            // Switch interactif et sécurisé
            Box(
                modifier = Modifier
                    .width(44.px)
                    .height(24.px)
                    .borderRadius(12.px)
                    .backgroundColor(if (enabled) colors.success else colors.border)
                    .styleModifier {
                        property("position", "relative")
                        property("transition", "background 0.2s")
                        property("flex-shrink", "0")
                        property("opacity", if (isLoading) "0.5" else "1.0")
                        property("cursor", if (isLoading) "not-allowed" else "pointer")
                    }
                    .onClick {
                        if (!isLoading) {
                            onToggle(!enabled)
                        }
                    }
            ) {
                Box(
                    modifier = Modifier
                        .size(18.px)
                        .borderRadius(50.percent)
                        .backgroundColor(colors.onPrimary)
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "3px")
                            property("left", if (enabled) "23px" else "3px")
                            property("transition", "left 0.2s")
                            property("box-shadow", "0 1px 3px rgba(0,0,0,0.2)")
                        }
                )
            }
        }
        if (!isLast) Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
    }
}

@Composable
fun TwoFactorSetupModal(
    viewModel: AuthViewModel,
    colors: AuthColors,
    onDismiss: () -> Unit
) {
    val state = viewModel.uiState
    var verificationCode by remember { mutableStateOf("") }

    // Overlay (fond sombre flouté/semi-transparent)
    Box(
        modifier = Modifier
            .fillMaxSize()
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .backgroundColor(colors.surface)
            .styleModifier { property("z-index", "1000") },
        contentAlignment = Alignment.Center
    ) {
        // Modal Container
        Column(
            modifier = Modifier
                .width(420.px)
                .maxWidth(90.percent)
                .borderRadius(16.px)
                .backgroundColor(colors.surface)
                .border(1.px, LineStyle.Solid, colors.border)
                .padding(24.px)
                .gap(20.px)
                .styleModifier { property("box-shadow", "0 10px 25px rgba(0,0,0,0.2)") }
        ) {
            // Header (Titre + Bouton Fermer)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.gap(10.px)
                ) {
                    FaLock(modifier = Modifier.fontSize(18.px).color(colors.primary))
                    SpanText(
                        "Activer la Double Authentification",
                        modifier = Modifier
                            .fontSize(AuthTypography.body)
                            .fontWeight(FontWeight.Bold)
                            .color(colors.onBackground)
                    )
                }
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onDismiss() }
                ) {
                    FaXmark(modifier = Modifier.fontSize(16.px).color(colors.mutedText))
                }
            }

            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))

            // Si la vérification est validée, on affiche les Recovery Codes
            if (state.twoFactorBackupCodes != null) {
                Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                    SpanText(
                        "⚠️ Sauvegardez vos codes de secours !",
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .fontWeight(FontWeight.Bold)
                            .color(colors.warning)
                    )
                    SpanText(
                        "Si vous perdez l'accès à votre application d'authentification, ces codes seront le seul moyen de récupérer votre compte.",
                        modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.mutedText)
                    )

                    // Grille affichant les codes
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(colors.inputBg)
                            .borderRadius(8.px)
                            .padding(12.px)
                            .gap(6.px)
                    ) {
                        state.twoFactorBackupCodes.forEach { code ->
                            SpanText(
                                code,
                                modifier = Modifier
                                    .fontSize(AuthTypography.small)
                                    .fontWeight(FontWeight.Medium)
                                    .color(colors.onBackground)
                                    .styleModifier { property("font-family", "monospace") }
                            )
                        }
                    }

                    AuthButton(
                        text = "J'ai bien enregistré mes codes",
                        onClick = {
                            viewModel.complete2FASetup()
                            onDismiss()
                        },
                        modifier = Modifier.fillMaxWidth().padding(top = 8.px)
                    )
                }
            } else {
                // Étape 1 : Scan QR Code + Code à 6 chiffres
                Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
                    SpanText(
                        "1. Scannez ce QR Code avec Google Authenticator ou 1Password :",
                        modifier = Modifier.fontSize(AuthTypography.small).color(colors.onBackground)
                    )

                    // Zone QR Code
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(180.px)
                            .backgroundColor(colors.inputBg)
                            .borderRadius(12.px)
                            .border(1.px, LineStyle.Dashed, colors.border),
                        contentAlignment = Alignment.Center
                    ) {
                        if (state.qrCodeUrl != null) {
                            Img(
                                src = state.qrCodeUrl,
                                alt = "2FA QR Code",
                                //modifier = Modifier.size(160.px)
                            )
                        } else {
                            SpanText("Génération du QR Code...", modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.mutedText))
                        }
                    }

                    // Clé Secrète Textuelle (Option de secours si la caméra ne marche pas)
                    if (state.secretKey != null) {
                        Column(modifier = Modifier.gap(4.px)) {
                            SpanText("Clé secrète (si impossible de scanner) :", modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.mutedText))
                            SpanText(
                                state.secretKey,
                                modifier = Modifier
                                    .fontSize(AuthTypography.tiny)
                                    .fontWeight(FontWeight.Bold)
                                    .color(colors.primary)
                                    .styleModifier { property("font-family", "monospace") }
                            )
                        }
                    }

                    SpanText(
                        "2. Entrez le code à 6 chiffres généré par l'application :",
                        modifier = Modifier.fontSize(AuthTypography.small).color(colors.onBackground)
                    )

                    AuthTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = verificationCode,
                        onValueChange = { if (it.length <= 6) verificationCode = it },
                        placeholder = "000000",
                        type = InputType.Text,
                        leadingIcon = { FaKey(modifier = Modifier.color(colors.secondary)) }
                    )

                    // Message d'erreur
                    if (state.twoFactorSetupError != null) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(6.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(13.px))
                            SpanText(
                                state.twoFactorSetupError,
                                modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.error)
                            )
                        }
                    }

                    // Actions
                    Row(modifier = Modifier.fillMaxWidth().gap(10.px).padding(top = 8.px)) {
                        AuthButton(
                            text = "Annuler",
                            onClick = { onDismiss() },
                            modifier = Modifier.weight(1).styleModifier { property("opacity", "0.7") }
                        )
                        AuthButton(
                            text = "Vérifier & Activer",
                            onClick = { viewModel.verifyAndEnable2FA(verificationCode) },
                            loading = state.isToggling2FA,
                            disabled = verificationCode.length < 6,
                            modifier = Modifier.weight(1)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun SecondaryAuthSetupModal(
    viewModel: AuthViewModel,
    colors: AuthColors,
    isEmailAuth: Boolean,
    onDismiss: () -> Unit
) {
    val state = viewModel.uiState
    var inputValue by remember { mutableStateOf(if (isEmailAuth) state.userPhone.orEmpty() else state.userEmail.orEmpty()) }
    var otpCode by remember { mutableStateOf("") }
    var stepCodeSent by remember { mutableStateOf(false) }

    // Overlay (Fond sombre)
    Box(
        modifier = Modifier
            .fillMaxSize()
            .position(Position.Fixed)
            .top(0.px).left(0.px)
            .backgroundColor(Color.rgba(r = 0, g = 0, b = 0, a = 0.6f))
            .cursor(Cursor.Pointer)
            // Clic sur le fond pour fermer le modal
            .onClick { onDismiss() }
            .styleModifier { property("z-index", "1000") },
        contentAlignment = Alignment.Center
    ) {
        // Modal Container (Boîte blanche/surface)
        Column(
            modifier = Modifier
                .width(400.px)
                .maxWidth(90.percent)
                .borderRadius(16.px)
                .backgroundColor(colors.surface)
                .padding(24.px)
                .gap(16.px)
                .cursor(Cursor.Default) // Remet le curseur par défaut à l'intérieur
                // Empêche le clic à l'intérieur du modal de fermer l'overlay
                .onClick { it.stopPropagation() }
                .styleModifier { property("box-shadow", "0 10px 25px rgba(0,0,0,0.2)") }
        ) {
            // HEADER (Titre + Icône de fermeture)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (isEmailAuth) "Sécuriser par Numéro (SMS)" else "Sécuriser par Email",
                    modifier = Modifier.fontSize(AuthTypography.body).fontWeight(FontWeight.Bold).color(colors.onBackground)
                )

                // Bouton Croix pour fermer
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onDismiss() }
                ) {
                    FaXmark(modifier = Modifier.fontSize(16.px).color(colors.mutedText))
                }
            }

            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))

            if (!stepCodeSent) {
                // ÉTAPE 1 : Saisie de la coordonnée secondaire
                SpanText(
                    if (isEmailAuth) "Entrez votre numéro de téléphone pour recevoir le code SMS :" else "Entrez votre adresse email :",
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
                )

                AuthTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = inputValue,
                    onValueChange = { inputValue = it },
                    placeholder = if (isEmailAuth) "+237 6XX XX XX XX" else "exemple@domaine.com",
                    type = InputType.Text,
                    leadingIcon = {
                        if (isEmailAuth) FaPhone(modifier = Modifier.color(colors.secondary))
                        else FaEnvelope(modifier = Modifier.color(colors.secondary))
                    }
                )

                // Affichage d'erreur éventuelle
                if (state.twoFactorSetupError != null) {
                    SpanText(
                        state.twoFactorSetupError,
                        modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.error)
                    )
                }

                Row(modifier = Modifier.fillMaxWidth().gap(10.px)) {
                    AuthButton(
                        text = "Annuler",
                        onClick = { onDismiss() },
                        modifier = Modifier.weight(1).styleModifier { property("opacity", "0.7") }
                    )
                    AuthButton(
                        text = "Envoyer le code",
                        onClick = {
                            viewModel.sendOTPToSecondaryChannel(inputValue) {
                                stepCodeSent = true
                            }
                        },
                        loading = state.isToggling2FA,
                        disabled = inputValue.isBlank(),
                        modifier = Modifier.weight(1)
                    )
                }
            } else {
                // ÉTAPE 2 : Saisie du code OTP reçu
                SpanText(
                    "Un code à 6 chiffres a été envoyé à $inputValue :",
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.mutedText)
                )

                AuthTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = otpCode,
                    onValueChange = { if (it.length <= 6) otpCode = it },
                    placeholder = "123456",
                    type = InputType.Text,
                    leadingIcon = { FaKey(modifier = Modifier.color(colors.secondary)) }
                )

                // Affichage d'erreur éventuelle
                if (state.twoFactorSetupError != null) {
                    SpanText(
                        state.twoFactorSetupError,
                        modifier = Modifier.fontSize(AuthTypography.tiny).color(colors.error)
                    )
                }

                Row(modifier = Modifier.fillMaxWidth().gap(10.px)) {
                    AuthButton(
                        text = "Retour",
                        onClick = { stepCodeSent = false },
                        modifier = Modifier.weight(1).styleModifier { property("opacity", "0.7") }
                    )
                    AuthButton(
                        text = "Valider et Activer",
                        onClick = { viewModel.verifyOTPAndEnable2FA(inputValue, otpCode) },
                        loading = state.isToggling2FA,
                        disabled = otpCode.length < 6,
                        modifier = Modifier.weight(1)
                    )
                }
            }
        }
    }
}




